"use client";

import { FormEvent, MouseEvent, ReactNode, useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.15,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, threshold, rootMargin]);
  return {
    ref,
    isVisible
  };
}
function RevealSection({
  children,
  className = ""
}: {
  children: ReactNode;
  className?: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <div ref={ref} className={`${className} transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
      {children}
    </div>;
}
function CtaButton({
  href,
  children,
  onClick,
  variant = "primary"
}: {
  href: string;
  children: ReactNode;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  variant?: "primary" | "secondary";
}) {
  return <a href={href} onClick={onClick} className={`group relative inline-flex h-14 items-center justify-center overflow-hidden rounded-full border px-8 text-base font-semibold transition-all duration-300 hover:-translate-y-0.5 ${variant === "primary" ? "border-white/10 bg-white text-slate-900 shadow-[0_12px_32px_rgba(15,23,42,0.22)] hover:shadow-[0_18px_42px_rgba(15,23,42,0.28)]" : "border-white/16 bg-white/10 text-white shadow-[0_12px_28px_rgba(15,23,42,0.14)] backdrop-blur-sm hover:border-white/28 hover:bg-white/14 hover:shadow-[0_18px_40px_rgba(15,23,42,0.22)]"}`}>
      <span className="relative z-10">{children}</span>
    </a>;
}
const suitableFor = ["с задержками развития", "с аутизмом", "с синдромом Дауна", "с СДВГ", "с трудностями поведения", "с низкой самостоятельностью", "с трудностями социализации"];
const whyMovement = ["улучшается поведение", "развивается речь", "появляется самоконтроль", "повышается уверенность в себе", "развивается навык игры", "улучшается взаимодействие с окружающими"];
const includedItems = [{
  title: "обучение через движение",
  sub: "баланс, моторика, координация, выносливость"
}, {
  title: "развитие самоконтроля"
}, {
  title: "развитие уверенности в себе"
}, {
  title: "навыки самообслуживания"
}, {
  title: "обучение роликам и велосипеду"
}, {
  title: "прогулки и выездные мероприятия"
}, {
  title: "катание на лошадях"
}, {
  title: "физический массаж"
}, {
  title: "стрижка"
}, {
  title: "обед"
}];
const processSteps = [{
  title: "Знакомство и адаптация",
  text: ["В первый день ребёнок приезжает вместе с родителем.", "Проводится беседа, анкетирование и мягкая адаптация к новой среде.", "В первые дни проходит первичная диагностика — без давления и стресса."]
}, {
  title: "Индивидуальная программа",
  text: ["В течение первых дней мы подбираем индивидуальную программу."],
  listTitle: "Родители получают:",
  list: ["результаты диагностики", "рекомендации", "план занятий"]
}, {
  title: "Основной курс",
  text: ["Ребёнок ежедневно приезжает в центр на трансфере."],
  listTitle: "Родители получают:",
  list: ["ежедневные отчёты", "фото и видео занятий", "возможность дополнительных консультаций"]
}, {
  title: "Контроль результатов",
  text: ["Мы регулярно оцениваем динамику и корректируем программу."]
}, {
  title: "Финальная встреча",
  text: ["В конце курса родители получают:"],
  list: ["итоговое видео", "заключение", "рекомендации на будущее"]
}];
const results = ["ребёнок лучше удерживает баланс", "становится увереннее", "увеличивается внимание", "появляется контакт", "развивается самостоятельность", "улучшается социализация", "развивается игровой навык", "формируются бытовые навыки", "развивается речь", "снижается пищевая избирательность"];
const whyParentsChoose = ["индивидуальный наставник", "постоянный контроль", "ежедневные отчёты", "видеофиксация прогресса", "комплексный подход", "работа в команде с родителями"];
const diagnostics = ["моторика", "координация", "баланс", "зрительный контакт", "понимание речи", "продолжительность выполнения действия"];
export default function ProgramDevelopmentThroughMovementPage() {
  const handleScrollToContact = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const form = document.getElementById("contact-form");
    form?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  };
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    window.location.href = "/thank-you";
  };
  return <main className="bg-[#f7f7f4] text-slate-950">
      <section className="relative overflow-hidden bg-[#0f172a] px-6 py-20 text-white md:px-12 md:py-24">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.06),transparent_34%)]" />
          <div className="absolute left-0 top-0 h-80 w-80 rounded-full bg-white/[0.03] blur-3xl" />
          <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-[#166534]/[0.08] blur-3xl" />
        </div>

        <RevealSection className="relative z-10 mx-auto flex max-w-7xl flex-col-reverse items-center justify-between gap-12 md:flex-row">
          <div className="flex flex-1 flex-col items-center text-center md:items-start md:text-left">
            <div className="mb-5 inline-flex items-center rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-[11px] font-medium uppercase tracking-[0.22em] text-white/72 backdrop-blur-md sm:text-xs">
              Программа развития через движение
            </div>

            <h1 className="mb-6 max-w-3xl font-default text-4xl font-bold tracking-[-0.04em] text-white sm:text-5xl md:text-6xl lg:text-7xl">
              Программа развития
              <br />
              <span className="text-[#166534]">через движение</span>
            </h1>

            <div className="mb-8 max-w-2xl space-y-4 font-default text-lg leading-relaxed text-white/78 md:text-xl">
              <p>
                Мы помогаем «собирать» ребёнка через движение — формируем базу,
                без которой развитие невозможно.
              </p>
              <p>
                Без умения владеть своим телом развитие не будет таким
                интенсивным, как хотелось бы.
              </p>
              <p>
                Наша задача — помочь ребёнку поверить в себя, раскрыться миру и
                подготовиться к учебной и социальной среде.
              </p>
            </div>

            <CtaButton href="#contact-form" onClick={handleScrollToContact}>
              Записаться на диагностику
            </CtaButton>
          </div>

          <div className="flex w-full flex-1 justify-center md:justify-end">
            <div className="relative w-[290px] sm:w-[340px] md:w-[420px] lg:w-[470px]">
              <div className="absolute -inset-4 rounded-[36px] bg-white/[0.04] blur-2xl" />
              <div className="relative overflow-hidden rounded-[36px] border border-white/12 bg-white/[0.06] p-3 shadow-[0_24px_60px_rgba(0,0,0,0.28)] backdrop-blur-md">
                <div className="overflow-hidden rounded-[28px]">
                  <img src="https://informed-gannet.10web.cloud/wp-content/uploads/2026/04/IMG_8792.png" alt="Программа развития через движение" className="h-[360px] w-full object-cover md:h-[460px]" />
                </div>
              </div>
            </div>
          </div>
        </RevealSection>
      </section>

      <section className="relative overflow-hidden bg-cover bg-center bg-no-repeat py-24">
        <div className="absolute inset-0" style={{
        backgroundImage: "url('https://informed-gannet.10web.cloud/wp-content/uploads/2026/04/IMG_8570.png')",
        backgroundSize: "cover",
        backgroundPosition: "center"
      }} />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(15,23,42,0.72)_0%,rgba(15,23,42,0.46)_18%,rgba(255,255,255,0.10)_44%,rgba(255,255,255,0.16)_100%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.14)_0%,rgba(255,255,255,0.08)_28%,rgba(255,255,255,0.16)_100%)]" />

        <RevealSection className="container relative z-10 mx-auto px-4">
          <div className="mx-auto mb-14 max-w-3xl text-center">
            <div className="mb-5 inline-flex items-center rounded-full border border-white/50 bg-white/36 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.22em] text-slate-700 shadow-[0_8px_24px_rgba(15,23,42,0.06)] backdrop-blur-md sm:text-xs">
              Кому подходит программа
            </div>

            <h2 className="mb-5 font-default text-3xl font-bold tracking-[-0.03em] text-white sm:text-4xl md:text-5xl">
              Кому подходит <span className="text-[#166534]">программа</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
            {suitableFor.map(item => <div key={item} className="rounded-[40px] border border-white/55 bg-white/42 p-8 shadow-[0_14px_40px_rgba(15,23,42,0.08)] backdrop-blur-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-white/50 bg-white/35 shadow-[0_6px_18px_rgba(15,23,42,0.05)] backdrop-blur-md">
                  <span className="text-2xl text-[#166534]">✓</span>
                </div>
                <p className="font-default text-lg font-semibold leading-relaxed text-slate-900">
                  {item}
                </p>
              </div>)}
          </div>
        </RevealSection>
      </section>

      <section className="relative overflow-hidden bg-[#f7f7f4] py-24">
        <RevealSection className="container mx-auto px-4">
          <div className="mx-auto max-w-5xl rounded-[44px] border border-slate-200/70 bg-white/80 p-8 shadow-[0_20px_50px_rgba(15,23,42,0.06)] backdrop-blur-md md:p-12">
            <div className="mb-5 inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-[11px] font-medium uppercase tracking-[0.22em] text-slate-600 shadow-sm sm:text-xs">
              Почему мы используем движение как основу развития
            </div>

            <h2 className="mb-8 font-default text-3xl font-bold tracking-[-0.03em] text-slate-950 sm:text-4xl md:text-5xl">
              Почему мы используем движение
              <br />
              <span className="text-[#166534]">как основу развития</span>
            </h2>

            <div className="mb-8 space-y-4 font-default text-lg leading-relaxed text-slate-700">
              <p>Спорт — это инструмент для развития мозга.</p>
              <p>Когда ребёнок начинает лучше владеть своим телом:</p>
            </div>

            <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2">
              {whyMovement.map(item => <div key={item} className="flex items-start gap-3 rounded-[24px] border border-slate-200/70 bg-[#f8fafc] p-5">
                  <span className="mt-1 text-[#166534]">✓</span>
                  <span className="font-default text-base font-medium leading-relaxed text-slate-800">
                    {item}
                  </span>
                </div>)}
            </div>

            <p className="font-default text-lg leading-relaxed text-slate-700">
              Через движение ребёнок учится понимать мир и своё место в нём.
            </p>
          </div>
        </RevealSection>
      </section>

      <section className="relative overflow-hidden bg-cover bg-center bg-no-repeat py-24" style={{
      backgroundImage: "url('https://informed-gannet.10web.cloud/wp-content/uploads/2026/04/IMG_8568.png')"
    }}>
        <div className="absolute inset-0 bg-slate-950/12" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.16)_0%,rgba(255,255,255,0.10)_30%,rgba(15,23,42,0.28)_68%,rgba(15,23,42,0.68)_100%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.12)_0%,rgba(255,255,255,0.08)_34%,rgba(255,255,255,0.04)_60%,rgba(15,23,42,0.18)_100%)]" />

        <RevealSection className="container relative z-10 mx-auto px-4">
          <div className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl border-l-2 border-[#166534] pl-6">
              <div className="mb-4 inline-flex items-center rounded-full border border-white/55 bg-white/36 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.22em] text-slate-700 shadow-[0_8px_24px_rgba(15,23,42,0.06)] backdrop-blur-md sm:text-xs">
                Что входит в программу
              </div>

              <h2 className="mb-4 font-default text-3xl font-bold tracking-[-0.03em] text-slate-950 sm:text-4xl md:text-5xl">
                Что входит в <span className="text-[#166534]">программу</span>
              </h2>
            </div>

            <CtaButton href="/services">Смотреть все услуги</CtaButton>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {includedItems.map(item => <div key={item.title} className="rounded-[40px] border border-white/55 bg-white/42 p-8 shadow-[0_14px_40px_rgba(15,23,42,0.08)] backdrop-blur-lg">
                <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border border-white/50 bg-white/35 shadow-[0_6px_18px_rgba(15,23,42,0.05)] backdrop-blur-md">
                  <span className="text-2xl text-[#166534]">•</span>
                </div>
                <h3 className="mb-3 font-default text-xl font-semibold tracking-[-0.02em] text-slate-950">
                  {item.title}
                </h3>
                {"sub" in item && item.sub ? <p className="font-default text-sm leading-6 text-slate-700">
                    {item.sub}
                  </p> : null}
              </div>)}
          </div>
        </RevealSection>
      </section>

      <section className="relative overflow-hidden bg-[#0f172a] py-24 text-white">
        <RevealSection className="container relative z-10 mx-auto px-4">
          <div className="mx-auto mb-14 max-w-3xl text-center">
            <div className="mb-5 inline-flex items-center rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-[11px] font-medium uppercase tracking-[0.22em] text-white/72 backdrop-blur-md sm:text-xs">
              Как проходит программа
            </div>

            <h2 className="mb-5 font-default text-3xl font-bold tracking-[-0.03em] text-white sm:text-4xl md:text-5xl">
              Как проходит <span className="text-[#166534]">программа</span>
            </h2>
          </div>

          <div className="mx-auto max-w-5xl space-y-6">
            {processSteps.map((step, index) => <div key={step.title} className="rounded-[36px] border border-white/12 bg-white/[0.08] p-8 backdrop-blur-xl shadow-[0_16px_46px_rgba(0,0,0,0.18)]">
                <div className="mb-6 flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/[0.08] text-lg font-bold text-[#d8c27a]">
                    {index + 1}
                  </div>
                  <h3 className="font-default text-2xl font-semibold tracking-[-0.03em] text-white">
                    {step.title}
                  </h3>
                </div>

                <div className="space-y-3 font-default text-base leading-7 text-white/82">
                  {step.text.map(paragraph => <p key={paragraph}>{paragraph}</p>)}
                </div>

                {step.list ? <div className="mt-6">
                    {step.listTitle ? <p className="mb-3 font-default text-base font-semibold text-white">
                        {step.listTitle}
                      </p> : null}
                    <ul className="space-y-3">
                      {step.list.map(item => <li key={item} className="flex items-start gap-3 text-white/82">
                          <span className="mt-1 text-[#d8c27a]">✓</span>
                          <span className="font-default text-base leading-7">{item}</span>
                        </li>)}
                    </ul>
                  </div> : null}
              </div>)}
          </div>
        </RevealSection>
      </section>

      <section className="relative overflow-hidden bg-[#f7f7f4] py-24">
        <RevealSection className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl rounded-[40px] border border-slate-200/70 bg-white/80 p-8 shadow-[0_20px_50px_rgba(15,23,42,0.06)] backdrop-blur-md md:p-12">
            <div className="mb-5 inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-[11px] font-medium uppercase tracking-[0.22em] text-slate-600 shadow-sm sm:text-xs">
              Формат занятий
            </div>

            <h2 className="mb-6 font-default text-3xl font-bold tracking-[-0.03em] text-slate-950 sm:text-4xl md:text-5xl">
              Формат <span className="text-[#166534]">занятий</span>
            </h2>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-[1.3fr_0.9fr]">
              <div className="space-y-4 font-default text-lg leading-relaxed text-slate-700">
                <p>Полностью индивидуальное сопровождение.</p>
                <p>Минимальная продолжительность курса — 1 месяц.</p>
              </div>

              <div className="rounded-[28px] border border-slate-200 bg-[#f8fafc] p-6">
                <p className="mb-3 font-default text-sm font-semibold uppercase tracking-[0.18em] text-[#166534]">
                  График работы
                </p>
                <div className="space-y-3 font-default text-base text-slate-700">
                  <div className="flex items-center justify-between gap-4">
                    <span>Будни</span>
                    <span className="font-semibold">09:00–17:00</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span>Суббота</span>
                    <span className="font-semibold">09:00–14:00</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </RevealSection>
      </section>

      <section className="relative overflow-hidden bg-white py-24">
        <RevealSection className="container mx-auto px-4">
          <div className="mx-auto mb-14 max-w-3xl text-center">
            <div className="mb-5 inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-[11px] font-medium uppercase tracking-[0.22em] text-slate-600 shadow-sm sm:text-xs">
              Какие результаты видят родители
            </div>

            <h2 className="mb-5 font-default text-3xl font-bold tracking-[-0.03em] text-slate-950 sm:text-4xl md:text-5xl">
              Какие результаты
              <br />
              <span className="text-[#166534]">видят родители</span>
            </h2>
          </div>

          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 md:grid-cols-2">
            {results.map(item => <div key={item} className="rounded-[32px] border border-slate-200/70 bg-[#f8fafc] p-6 shadow-[0_14px_40px_rgba(15,23,42,0.06)]">
                <div className="flex items-start gap-3">
                  <span className="mt-1 text-[#166534]">›</span>
                  <span className="font-default text-base font-medium leading-7 text-slate-800">
                    {item}
                  </span>
                </div>
              </div>)}
          </div>

          <div className="mx-auto mt-10 max-w-5xl rounded-[32px] border border-slate-200/70 bg-white p-8 shadow-[0_14px_40px_rgba(15,23,42,0.06)]">
            <p className="mb-4 font-default text-lg font-semibold text-slate-900">
              Первые видимые результаты обычно появляются к концу третьей недели.
            </p>
            <p className="font-default text-base leading-7 text-slate-700">
              Результаты зависят от начального уровня ребёнка и продолжительности
              курса. Для закрепления результатов рекомендуется проходить курс
              регулярно несколько раз в течение года.
            </p>
          </div>
        </RevealSection>
      </section>

      <section className="relative overflow-hidden bg-[#f7f7f4] py-24">
        <RevealSection className="container mx-auto px-4">
          <div className="mx-auto mb-14 max-w-3xl text-center">
            <div className="mb-5 inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-[11px] font-medium uppercase tracking-[0.22em] text-slate-600 shadow-sm sm:text-xs">
              Почему родители выбирают нас
            </div>

            <h2 className="mb-5 font-default text-3xl font-bold tracking-[-0.03em] text-slate-950 sm:text-4xl md:text-5xl">
              Почему родители
              <br />
              <span className="text-[#166534]">выбирают нас</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {whyParentsChoose.map(item => <div key={item} className="rounded-[32px] border border-slate-200/70 bg-white p-8 shadow-[0_14px_40px_rgba(15,23,42,0.06)]">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#166534]/[0.06]">
                  <span className="text-xl text-[#166534]">♥</span>
                </div>
                <p className="font-default text-lg font-semibold leading-relaxed text-slate-900">
                  {item}
                </p>
              </div>)}
          </div>
        </RevealSection>
      </section>

      <section className="relative overflow-hidden bg-[#0f172a] py-24 text-white">
        <RevealSection className="container relative z-10 mx-auto px-4">
          <div className="mx-auto mb-14 max-w-3xl text-center">
            <div className="mb-5 inline-flex items-center rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-[11px] font-medium uppercase tracking-[0.22em] text-white/72 backdrop-blur-md sm:text-xs">
              Что проверяется на диагностике
            </div>

            <h2 className="mb-5 font-default text-3xl font-bold tracking-[-0.03em] text-white sm:text-4xl md:text-5xl">
              Что проверяется
              <br />
              <span className="text-[#166534]">на диагностике</span>
            </h2>
          </div>

          <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {diagnostics.map(item => <div key={item} className="rounded-[32px] border border-white/12 bg-white/[0.08] p-7 shadow-[0_16px_46px_rgba(0,0,0,0.18)] backdrop-blur-xl">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/[0.08]">
                  <span className="text-xl text-[#d8c27a]">•</span>
                </div>
                <p className="font-default text-lg font-semibold leading-relaxed text-white">
                  {item}
                </p>
              </div>)}
          </div>
        </RevealSection>
      </section>

      <section id="contact-form" className="relative overflow-hidden bg-[#0f172a] px-6 py-20 text-white md:px-12 md:py-24">
        <RevealSection className="relative z-10 mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="rounded-[40px] border border-white/10 bg-white/[0.05] p-8 backdrop-blur-md md:p-10">
              <div className="mb-5 inline-flex items-center rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-[11px] font-medium uppercase tracking-[0.22em] text-white/72 backdrop-blur-md sm:text-xs">
                Запишите ребёнка на диагностику
              </div>

              <h2 className="mb-6 font-default text-3xl font-bold tracking-[-0.03em] text-white sm:text-4xl md:text-5xl">
                Запишите ребёнка
                <br />
                <span className="text-[#166534]">на диагностику</span>
              </h2>

              <div className="space-y-4 font-default text-lg leading-relaxed text-white/78">
                <p>К нам приезжают семьи из разных стран.</p>
                <p>
                  Мы сопровождаем ребёнка и родителей на каждом этапе программы.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="rounded-[40px] border border-white/10 bg-white/[0.08] p-8 shadow-[0_24px_60px_rgba(0,0,0,0.24)] backdrop-blur-xl md:p-10">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-white/80">
                    Имя родителя
                  </span>
                  <input required type="text" className="h-12 rounded-2xl border border-white/10 bg-white/[0.06] px-4 text-white outline-none" />
                </label>

                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-white/80">
                    Телефон
                  </span>
                  <input required type="tel" className="h-12 rounded-2xl border border-white/10 bg-white/[0.06] px-4 text-white outline-none" />
                </label>

                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-white/80">Email</span>
                  <input type="email" className="h-12 rounded-2xl border border-white/10 bg-white/[0.06] px-4 text-white outline-none" />
                </label>

                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-white/80">Страна</span>
                  <input type="text" className="h-12 rounded-2xl border border-white/10 bg-white/[0.06] px-4 text-white outline-none" />
                </label>

                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-white/80">
                    Имя ребёнка
                  </span>
                  <input required type="text" className="h-12 rounded-2xl border border-white/10 bg-white/[0.06] px-4 text-white outline-none" />
                </label>

                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-white/80">
                    Возраст ребёнка
                  </span>
                  <input type="text" className="h-12 rounded-2xl border border-white/10 bg-white/[0.06] px-4 text-white outline-none" />
                </label>

                <label className="flex flex-col gap-2 md:col-span-2">
                  <span className="text-sm font-medium text-white/80">
                    Комментарий
                  </span>
                  <textarea rows={5} className="rounded-[24px] border border-white/10 bg-white/[0.06] px-4 py-3 text-white outline-none" />
                </label>
              </div>

              <div className="mt-8">
                <button type="submit" className="group relative inline-flex h-14 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-white px-10 text-base font-semibold text-slate-900 shadow-[0_12px_32px_rgba(15,23,42,0.22)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_42px_rgba(15,23,42,0.28)]">
                  Отправить заявку
                </button>
              </div>
            </form>
          </div>
        </RevealSection>
      </section>
    </main>;
}